// frontend/src/components/FinanceReport/Accounting/components/accounts/company-accounts/CompanyAccountDetailPage.jsx

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../../../../../UI/Button";
import ConfirmModal from "../../../../../UI/ConfirmModal";
import {
  getAccountById,
  getAllCurrencies,
  getAllProfitAllocations,
  deleteProfitAllocation,
} from "./requests";
import ProfitAllocationFormModal from "./ProfitAllocationFormModal";
import ProfitAllocationDocumentsModal from "./ProfitAllocationDocumentsModal";

const RATES_URL = "https://open.er-api.com/v6/latest/USD";

const currencySymbols = {
  USD: "$", EUR: "€", RUB: "₽", CNY: "¥", HKD: "HK$", USDT: "₮",
};

const STATUS_LABELS = {
  PENDING: "Ожидает",
  PAID: "Получено",
};

export default function CompanyAccountDetailPage() {
  const { accountId } = useParams();
  const navigate = useNavigate();

  const [account, setAccount] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [items, setItems] = useState([]);
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");

  const [isOpen, setIsOpen] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [txToDelete, setTxToDelete] = useState(null);
  const [docsItem, setDocsItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [acc, curList, allProfit] = await Promise.all([
        getAccountById(accountId).catch(() => null),
        getAllCurrencies().catch(() => []),
        getAllProfitAllocations().catch(() => []),
      ]);
      setAccount(acc);
      setCurrencies(curList);
      setItems(allProfit.filter((p) => String(p.account) === String(accountId)));
    } catch (err) {
      setError(err.message || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    fetch(RATES_URL)
      .then((r) => r.json())
      .then((d) => setRates(d.rates))
      .catch(console.error);
  }, []);

  const accountCurrency = account?.currency || items[0]?.currency_code || "USD";

  const currencySymbol =
    currencySymbols[accountCurrency] || accountCurrency;

  const currencyId = useMemo(() => {
    const found = currencies.find((c) => c.code === accountCurrency);
    return found?.id ?? null;
  }, [currencies, accountCurrency]);

  // Баланс — только подтверждённые (PAID) движения
  const balance = useMemo(() => {
    let bal = 0;
    items.forEach((p) => {
      if (p.payment_status !== "PAID") return;   // ← фильтр
      const amt = Number(p.amount || 0);
      if (p.type === "income") bal += amt;
      else bal -= amt;
    });
    return bal;
  }, [items]);

  // Сумма «в ожидании» — только PENDING, для отдельной строки в шапке
  const pendingSum = useMemo(() => {
    let sum = 0;
    items.forEach((p) => {
      if (p.payment_status === "PAID") return;
      const amt = Number(p.amount || 0);
      if (p.type === "income") sum += amt;
      else sum -= amt;
    });
    return sum;
  }, [items]);

  const totalCny = useMemo(() => {
    if (!rates) return 0;
    const cnyRate = rates.CNY || 1;
    const accRate = rates[accountCurrency] || 1;
    return balance * (cnyRate / accRate);
  }, [balance, rates, accountCurrency]);

  const openCreate = () => {
    setWarning("");
    setEditingTx(null);
    setIsOpen(true);
  };

  const openEdit = (tx) => {
    setWarning("");
    setEditingTx(tx);
    setIsOpen(true);
  };

  const handleSaved = async ({ warning: w } = {}) => {
    setIsOpen(false);
    setEditingTx(null);
    setWarning(w || "");
    await load();
  };

  const handleDelete = async () => {
    if (!txToDelete) return;
    try {
      await deleteProfitAllocation(txToDelete.id);
      setTxToDelete(null);
      await load();
    } catch (err) {
      setError(err.message || "Не удалось удалить");
    }
  };

  if (loading) return <div className="max-w-6xl mx-auto mt-10 px-4">Загрузка…</div>;
  if (!account && items.length === 0) {
    return <div className="max-w-6xl mx-auto mt-10 px-4 text-red-600">{error || "Счёт не найден"}</div>;
  }

  return (
    <div className="max-w-6xl mx-auto mt-10 px-4">
      <button
        type="button"
        onClick={() => navigate("/accounting?tab=accounts")}
        className="mb-4 text-sm text-slate-500 hover:text-blue-600"
      >
        ← Все счета
      </button>

      <h1 className="text-2xl font-bold">
        {account?.name || items[0]?.account_name || `Счёт #${accountId}`}
      </h1>

      <p className="text-gray-600 mb-2">
        Баланс : {currencySymbol} {balance.toFixed(2)}
      </p>

      <p className="mb-1 text-sm text-amber-700">
        В ожидании : {currencySymbol} {pendingSum.toFixed(2)}
      </p>

      <p className="mb-4 font-semibold text-gray-800">
        ≈ ¥ {totalCny.toFixed(2)} (CNY)
      </p>

      <Button onClick={openCreate} variant="primary">
        Добавить транзакцию
      </Button>

      {error && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {warning && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <pre className="whitespace-pre-wrap text-xs">{warning}</pre>
        </div>
      )}

      <div className="mt-6 border rounded-xl overflow-hidden">
        <div className="grid grid-cols-7 bg-gray-100 p-3 font-semibold text-sm">
          <div>Дата</div>
          <div>Сумма</div>
          <div>Платёж через</div>
          <div>Комментарий</div>
          <div>Статус</div>
          <div>Документы</div>
          <div className="text-right"></div>
        </div>

        {items.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            Движений пока нет
          </div>
        ) : (
          items.map((tx) => {
            const docCount = tx.documents?.length || 0;
            const isIncome = tx.type === "income";
            const isDividends = tx.type === "dividends";
            const sign = isIncome ? "+" : "-";
            const color = isIncome
              ? "text-green-600"
              : isDividends
              ? "text-blue-600"
              : "text-red-600";

            const isPaid = tx.payment_status === "PAID";

            return (
              <div
                key={tx.id}
                className="grid grid-cols-7 p-3 border-t items-center text-sm"
              >
                <div>{tx.date || "-"}</div>

                <div className={color}>
                  {sign}
                  {currencySymbol}
                  {Number(tx.amount).toFixed(2)}
                </div>

                <div className="truncate">{tx.payment_agent || "-"}</div>

                <div className="truncate">{tx.comment || "-"}</div>

                <div>
                  <span
                    className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                      isPaid
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {STATUS_LABELS[tx.payment_status] || tx.payment_status || "-"}
                  </span>
                </div>

                <div>
                  <button
                    type="button"
                    onClick={() => setDocsItem(tx)}
                    className="text-xs text-blue-700 underline hover:text-blue-900"
                  >
                    Документы ({docCount})
                  </button>
                </div>

                <div className="flex gap-2 justify-end">
                  <Button variant="edit" onClick={() => openEdit(tx)}>
                    Редактировать
                  </Button>
                  <Button variant="danger" onClick={() => setTxToDelete(tx)}>
                    Удалить
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>

      <ProfitAllocationFormModal
        open={isOpen}
        mode={editingTx ? "edit" : "create"}
        initial={editingTx}
        accountId={Number(accountId)}
        currencyId={currencyId}
        onClose={() => {
          setIsOpen(false);
          setEditingTx(null);
        }}
        onSaved={handleSaved}
      />

      <ProfitAllocationDocumentsModal
        open={Boolean(docsItem)}
        item={docsItem}
        onClose={() => setDocsItem(null)}
        onDeleted={load}
      />

      <ConfirmModal
        isOpen={Boolean(txToDelete)}
        onClose={() => setTxToDelete(null)}
        onConfirm={handleDelete}
        title="Удалить транзакцию?"
        description="Это действие нельзя отменить"
        confirmText="Удалить"
      />
    </div>
  );
}